import { useState } from 'react';
import { get, post } from '../api';
import { describe } from '../errors';
import { useLoad } from '../hooks';

interface Tag {
  id: number;
  label: string;
}

/**
 * Tags on a record: the chosen ones as removable chips, and a small panel to
 * add more or make a new one. Tags decide which contractors see offered work.
 */
export function TagPicker(props: {
  label: string;
  value: number[];
  onChange(ids: number[]): void;
}) {
  const [tick, setTick] = useState(0);
  const tags = useLoad(() => get<{ items: Tag[] }>('/api/tags'), [tick]);
  const [open, setOpen] = useState(false);
  const [fresh, setFresh] = useState('');
  const [error, setError] = useState('');
  const all = Array.isArray(tags.data?.items) ? tags.data!.items : [];
  const chosen = all.filter((t) => props.value.includes(t.id));

  const toggle = (id: number) =>
    props.onChange(
      props.value.includes(id) ? props.value.filter((x) => x !== id) : [...props.value, id],
    );

  async function create() {
    setError('');
    if (fresh.trim() === '') return;
    try {
      const r = await post<{ id: number }>('/api/tags', { label: fresh.trim() });
      setFresh('');
      setTick((t) => t + 1);
      props.onChange([...props.value, r.id]);
    } catch (e) {
      setError(describe(e).problems[0] ?? 'Could not add the tag.');
    }
  }

  return (
    <div className="tag-picker" role="group" aria-label={props.label}>
      <ul className="chips">
        {chosen.map((t) => (
          <li key={t.id} className="chip">
            {t.label}
            <button type="button" className="quiet" onClick={() => toggle(t.id)}>
              ×<span className="sr-only"> Remove {t.label}</span>
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="quiet add-tag"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        + Add tag
      </button>
      {open && (
        <div className="popover">
          <div className="chips">
            {all.map((t) => (
              <button
                key={t.id}
                type="button"
                className={props.value.includes(t.id) ? 'chip on' : 'chip'}
                aria-pressed={props.value.includes(t.id)}
                onClick={() => toggle(t.id)}
              >
                {t.label}
              </button>
            ))}
            {all.length === 0 && <span className="muted">No tags yet.</span>}
          </div>
          <div className="row">
            <label>
              New tag
              <input value={fresh} onChange={(e) => setFresh(e.target.value)} />
            </label>
            <button type="button" onClick={() => void create()}>
              Add
            </button>
          </div>
          {error && (
            <p role="alert" className="error">
              {error}
            </p>
          )}
          <button type="button" className="quiet" onClick={() => setOpen(false)}>
            Done
          </button>
        </div>
      )}
    </div>
  );
}
